import React, { useState, useEffect, useContext } from 'react';
import {Form, Input, Tag, DatePicker, Checkbox, Row, Space, message, Select} from 'antd';
import axios from "@common/axios";
import SyllabusTopicConceptSelect from "@common/SyllabusTopicConceptSelect";
import {UserContext} from "@context/UserContext";
import {BrandContext} from "@context/BrandContext";
import {ContentContext} from "@context/ContentContext";
import TableFilterControls from "@common/TableFilterControls";
import PopConfirm from "@common/PopConfirm";
import ShareTrailDialog from "./ShareTrailDialog";
import AssignTrailDialog from "./AssignTrailDialog";
import TableFilterControlsForm from "@common/TableFilterControlsForm";
import TableFilterControlsSection from "@common/TableFilterControlsSection";
import Button from "@common/Button";
import TableAntD from "@common/TableAntD";
import {useFormFilterBadge} from "@hooks/useFormFilterBadge";
import TrailBuilder from "./TrailBuilder";
import DynamicContent from "../../nav/DynamicContent";

const { RangePicker } = DatePicker;

const TeacherTrails = (props) => {
    const {user} = useContext(UserContext);
    const brandConfig = useContext(BrandContext);
    const {setContentLoading} = useContext(ContentContext);
    const {pathwayType = 'TASK', sharedPathways = false, schoolPathways = false} = props;
    const [pathwaySearchForm] = Form.useForm();
    const [archived, setArchived] = useState(false);
    const [orderBy, setOrderBy] = useState('CREATED_DESC');
    const [selectedTrails, setSelectedTrails] = useState(() => new Map());
    const [selectedTrail, setSelectedTrail] = useState(null);
    const [globalTrailId, setGlobalTrailId] = useState(null);
    const [trails, setTrails] = useState([]);
    const [reload, setReload] = useState();
    const [error, setError] = useState();
    const [isDisableEdit, setDisableEdit] = useState(true);
    const [isDisableAssign, setDisableAssign] = useState(true);
    const [isDisableArchive, setDisableArchive] = useState(true);
    const [isDisableShare, setDisableShare] = useState(true);
    const [isDisableCopy, setDisableCopy] = useState(true);
    const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
    const [isAssignTrailDialogOpen, setAssignTrailDialogOpen] = useState(false);
    const {activeFilterCount, onLoadComplete, handleValuesChange: handleBadgeValuesChange} = useFormFilterBadge(pathwaySearchForm);
    const [trailId, setTrailId] = useState(null);
    const [createTrail, setCreateTrail] = useState(false);

    globalReactFunctions.refreshTeacherTasks = () => {
        setReload(new Date().getTime());
    };
    globalReactFunctions.setCreateTrail = () => {
        setCreateTrail(true);
        setTrailId(null);
    };
    globalReactFunctions.setEditTrail = trailId => {
        setCreateTrail(false);
        setTrailId(trailId);
    };
    globalReactFunctions.setViewTrailProgress = newTrail => {
        setCreateTrail(newTrail === true);
        setTrailId(null);
    };

    const addSelectedTrail = (key, value) => {
        setSelectedTrails(prevMap => {
            const newMap = new Map(prevMap);
            newMap.set(key, value);
            return newMap;
        });
    };

    const addAllSelectedTrails = (trails) => {
        setSelectedTrails(prevMap => {
            const newMap = new Map(prevMap);
            trails.forEach(trail => {
                newMap.set(trail.pathwayId, trail);
            });
            return newMap;
        });
    };

    const removeSelectedTrail = (key) => {
        setSelectedTrails(prevMap => {
            const newMap = new Map(prevMap);
            newMap.delete(key);
            return newMap;
        });
    };

    const clearSelectedTrails  = () => {
        setSelectedTrails(new Map());
    };

    useEffect(() => {
        let disableEdit = true;
        let disableAssign = true;
        let disableArchive = true;
        let disableShare = true;
        let disableCopy = true;
        for(const trail of selectedTrails.values()) {
            const owned = trail.ownerId === user.userId;
            if(!trail.archived && !trail.started && owned) {
                disableEdit = false;
            }
            if(trail.published) {
                disableAssign = false;
            }
            if(!trail.archived && !trail.activeAssignments && owned) {
                disableArchive = false;
            }
            if(trail.published && owned) {
                disableShare = false;
            }
            if(trail.hasContent) {
                disableCopy = false;
            }
        }
        setDisableEdit(disableEdit);
        setDisableAssign(selectedTrails.size !== 1 || disableAssign);
        setDisableArchive(disableArchive);
        setDisableShare(disableShare);
        setDisableCopy(disableCopy);
    }, [selectedTrails]);

    useEffect(() => {
        if (globalReactFunctions !== undefined && globalReactFunctions.createTrailFromDashboard) {
            globalReactFunctions.createTrailFromDashboard = undefined;
            createPathway('TASK');
        }

        if (globalReactFunctions !== undefined && globalReactFunctions.pathwayId && globalReactFunctions.editTrailFromDashboard) {
            let pathwayId = globalReactFunctions.pathwayId;
            globalReactFunctions.pathwayId = undefined;
            globalReactFunctions.editTrailFromDashboard = undefined;
            editPathway(pathwayId);
        }

        if (globalReactFunctions !== undefined && globalReactFunctions.pathwayId && globalReactFunctions.assignTrailFromDashboard) {
            let pathwayId = globalReactFunctions.pathwayId;
            globalReactFunctions.assignTrailFromDashboard = undefined;
            globalReactFunctions.pathwayId = undefined;
            openAssignPathwayDialog(pathwayId);
        }

        if (globalReactFunctions !== undefined && globalReactFunctions.pathwayId && globalReactFunctions.shareTrailFromDashboard) {
            let pathwayId = globalReactFunctions.pathwayId;
            globalReactFunctions.shareTrailFromDashboard = undefined;
            globalReactFunctions.pathwayId = undefined;
            setGlobalTrailId(pathwayId);
        }

        if (globalReactFunctions !== undefined && globalReactFunctions.pathwayId && globalReactFunctions.copyTrailFromDashboard) {
            let pathwayId = globalReactFunctions.pathwayId;
            globalReactFunctions.copyTrailFromDashboard = undefined;
            globalReactFunctions.pathwayId = undefined;
            copyPathway(pathwayId);
        }
    }, []);

    useEffect(() => {
        if(globalTrailId && trails.length > 0){
            const result = trails.find(trail => trail.pathwayId === globalTrailId);
            if(result){
                addSelectedTrail(globalTrailId, result);
                setIsShareDialogOpen(true);
                setGlobalTrailId(null);
            }
        }
    }, [globalTrailId, trails.length]);


    const columns = [
        {
            title: <Checkbox
                aria-label="select all tasks"
                checked={trails.length > 0 && selectedTrails.size === trails.length}
                onChange={(e) => onSelectAllRows(e.target.checked)}
            ><span className="ch-accessibility-label">select all tasks</span></Checkbox>,
            dataIndex: 'checkbox',
            width: 40,
            render: (_, record) => (
                <Checkbox
                    aria-label={`select ${record.pathwayName}`}
                    checked={selectedTrails.has(record.pathwayId)}
                    onChange={e => onSelectRow(e.target.checked, record)}
                ><span className="ch-accessibility-label">{'select ' + record.pathwayName}</span></Checkbox>
            )
        },
        {
            title: brandConfig.taskName + ' name',
            dataIndex: 'pathwayName',
            key: 'pathwayName',
            width: '40%',
            sorter: (a, b) => {
                return a.pathwayName.localeCompare(b.pathwayName);
            },
            sortDirections: ['ascend', 'descend'],
            render: (text, record) => <><a className="genLink" tabIndex={0} onClick={() => handleView(record)}>{record.pathwayName} {record.pathwayType == 'PATHWAY' ?  <Tag className="ch-tag-secondary">Template</Tag> :<></>}</a>{record.description && <><br/><span className="ch-text-note">{record.description}</span></>}</>
        },
        {
            title: 'Created',
            dataIndex: 'createDate',
            key: 'createDate',
            sorter: (a, b) => {
                return a.createDate - b.createDate;
            },
            sortDirections: ['ascend', 'descend'],
            render: (text, record) => {
                if (record.createDate == undefined) return '';

                const dateFormatted = user.dateTimeFormat.format(record.createDate);
                return (
                    <div>
                        <div>{dateFormatted}</div>
                        <div>{record.ownerFirstName} {record.ownerLastName}</div>
                    </div>
                );
            }
        },
        {
            title: 'Assigned',
            dataIndex: 'assignedDate',
            key: 'assignedDate',
            sorter: (a, b) => {
                const aDate = a.assignedDate;
                const bDate = b.assignedDate;

                if (aDate == null && bDate == null) return 0;
                if (aDate == null) return 1;
                if (bDate == null) return -1;

                return aDate - bDate;
            },
            sortDirections: ['ascend', 'descend'],
            render: (value, record) => {
                if (record.assignedDate == undefined) return '';

                const dateFormatted = user.dateTimeFormat.format(record.assignedDate);
                return dateFormatted;
            }
        },
        {
            title: 'Valid',
            dataIndex: 'valid',
            align: 'center',
            render: (value, record) => {
                if(record.valid) {
                    return 'Yes'
                } else {
                    return 'No'
                }
            }
        },
        {
            title: <span style={{whitespace: 'nowrap'}}>In use</span>,
            dataIndex: 'started',
            align: 'center',
            render: (value, record) => {
                if(record.started && record.activeAssignments) {
                    return 'Yes'
                } else if(!record.started && record.activeAssignments) {
                    return 'No'
                } else if(record.archived) {
                    return 'Archived'
                } else if(record.started) {
                    return 'Completed'
                } else {
                    return 'No'
                }
            }
        },
        {
            title: 'Shared',
            dataIndex: 'shared',
            align: 'center',
            render: (value) => value ? 'Yes' : 'No'
        }
    ];

    const onSelectRow = (checked, pathway) => {
        if(checked) {
            addSelectedTrail(pathway.pathwayId, pathway);
        } else {
            removeSelectedTrail(pathway.pathwayId);
        }
    };

    const onSelectAllRows = (checked) => {
        if (checked) {
            addAllSelectedTrails(trails);
        } else {
            clearSelectedTrails();
        }
    };

    const handleValuesChange = (changedValues, allValues) => {
        const keys = Object.keys(changedValues);
        const hasOtherKeys = keys.some(key => key !== 'pathwayName' && key !== 'dateRange');

        if (hasOtherKeys) {
            handleBadgeValuesChange();
        }

        if ('syllabusId' in changedValues || 'syllabusGroupId' in changedValues) {
            handleSearch();
        }
    }

    const handleSearch = () => {
        handleBadgeValuesChange();
        setReload(new Date().getTime());
    };

    const handleCreate = () => {
        createPathway(pathwayType);
    };

    const handleView = (record) => {
        if (record?.pathwayId !== undefined) {
            editPathway(record?.pathwayId);
        } else {
            message.error('Please select a single task to view.');
        }
    };

    const handleViewButton = () => {
        let pathwayId = undefined;
        if(selectedTrails && selectedTrails.size === 1) {
            pathwayId = selectedTrails.keys().next().value;
        }

        if (pathwayId !== undefined) {
            editPathway(pathwayId);
        } else {
            message.error('Please select a single task to view.');
        }
    };

    const handleAssign = () => {
        setAssignTrailDialogOpen(true);
        setSelectedTrail(selectedTrails.size === 1 ? selectedTrails.values().next().value : null);
    };

    const handleShare = () => {
        if (selectedTrails.size > 0) {
            setIsShareDialogOpen(true);
        } else {
            message.error('Please select a single task to share.');
        }
    };

    const copyPathways = (pathwayIds) => {
        const params = new URLSearchParams();
        pathwayIds.forEach(pathwayId => {
            params.append('pathwayIds', pathwayId);
        });

        axios.post('/trail/copyTrail.action', params.toString(), {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
            }
        }).then(response => {
            displayToastMessage(brandConfig.taskName + ' successfully copied', brandConfig.taskName + '/s archived successfully', 'success');
            setContentLoading(true);
            handleSearch();
        }).catch(error => {
                console.error(error);
                setError(error);
            })
            .finally(() => {
                setContentLoading(false);
            });
    }

    const handleCopy = () => {
        if (selectedTrails.size > 0) {
            copyPathways(Array.from(selectedTrails.keys()));
        } else {
            message.error('Please select a single task to copy.');
        }
    };

    const archivePathways = (pathwayIds) => {
        const params = new URLSearchParams();
        pathwayIds.forEach(pathwayId => {
            params.append('pathwayIds', pathwayId);
        });

        axios.post('/trail/archiveTrail.action', params.toString(), {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
            }
        }).then(response => {
                displayToastMessage(brandConfig.taskName + ' successfully archived', brandConfig.taskName + '/s archived successfully', 'success');
                setContentLoading(true);
                handleSearch();
            }).catch(error => {
                console.error(error);
                setError(error);
            }).finally(() => {
                setContentLoading(false);
            });
    }

    const handleArchive = () => {
        if (selectedTrails.size > 0) {
            archivePathways(Array.from(selectedTrails.keys()));
        } else {
            message.error('Please select a single task to archive.');
        }
    };

    const fetchData = (pagingParams, handleTableData, handlePagingData, abortController) => {
        const formValues = pathwaySearchForm.getFieldsValue();
        const pathwayName = formValues.pathwayName || undefined;
        const topicId = formValues.topicId || undefined;
        const conceptId = formValues.conceptId || undefined;
        const [startDate, endDate] = formValues.dateRange || [];

        const payload = {
            pageNumber:  pagingParams.pageNumber,
            pageSize: pagingParams.pageSize,
            pathwayGroupCode: undefined,
            pathwayType: pathwayType,
            schoolPathways: schoolPathways,
            sharedPathways: sharedPathways,
            pathwayName: pathwayName,
            archived: archived,
            topicId: topicId,
            conceptId: conceptId,
            startDate: startDate,
            endDate: endDate,
            orderBy: orderBy
        };

        axios.post('/web/ui/trail/search', payload, {
            headers: {
                'Content-Type': 'application/json'
            },
            signal: abortController.signal
        }).then(response => {
            clearSelectedTrails();
            handleTableData(response.data.currPageContents);
            setTrails(response.data.currPageContents);
            handlePagingData({
                pageSize: pagingParams.pageSize,
                totalResults: response.data.totalResults ?? 0,
            });
        })
        .catch(error => {
            console.error('Error loading data:', error);
            handleTableData([]);
        });
    };

    const getTableButtons = () => {
        return(
            <Row justify="start" className="margin-t--m margin-b--m">
                <Space>
                    <Button onClick={handleCreate}>Create</Button>
                    <Button type={'outline'} onClick={handleViewButton}
                            disabled={selectedTrails.size !== 1}>View</Button>
                    <Button type={'outline'} onClick={handleAssign}
                            disabled={isDisableAssign}>Assign</Button>
                    <Button type={'outline'} onClick={handleShare}
                            disabled={isDisableShare}>Share</Button>
                    <PopConfirm title={<span>Copy {brandConfig.taskName}?</span>}
                                okText='Copy'
                                onConfirm={handleCopy}>
                        <Button type={'outline'} disabled={isDisableCopy}>Copy</Button>
                    </PopConfirm>
                    <PopConfirm title={<span>Archive {brandConfig.taskName}?</span>}
                                okText='Archive'
                                onConfirm={handleArchive}>
                        <Button type={'outline'} disabled={isDisableArchive}>Archive</Button>
                    </PopConfirm>
                </Space>
            </Row>
        )
    }

    if (trailId != null) {
        return (
            <div>
                <TrailBuilder trailId={trailId} onExit={() => setTrailId(null)} />
            </div>
        )
    } else if (createTrail === true) {
        return (
            <div>
                <DynamicContent
                    url="/trail/showCreateTrail.action"
                    params={{pathwayType: pathwayType}}
                />
            </div>
        )
    }

    return (
        <div>
            <div>
                <TableFilterControls
                    filterCount={activeFilterCount}
                    showFilterButton={true}
                    filterContent={
                        <TableFilterControlsForm
                            form={pathwaySearchForm}
                            onFinish={handleSearch}
                            onValuesChange={handleValuesChange}
                            initialValues={{nameSearch: ''}}
                            useApplyButton={true}
                        >
                            <TableFilterControlsSection>
                                <Form.Item label="Task name" name="pathwayName">
                                    <Input />
                                </Form.Item>
                                <Form.Item name="contentCategory" label="Content category">
                                    <Select
                                        mode="multiple"
                                        value={archived ? ['archived'] : []}
                                        onChange={(values) => setArchived(values.includes('archived'))}
                                        allowClear={true}
                                    >
                                        <Select.Option value="archived">Archived only</Select.Option>
                                    </Select>
                                </Form.Item>
                                <Form.Item label="Created date" name="dateRange">
                                    <RangePicker format={user.getDateFormatString()} style={{width: '100%'}}/>
                                </Form.Item>
                            </TableFilterControlsSection>
                            <TableFilterControlsSection showDivider={false}>
                                <SyllabusTopicConceptSelect
                                    form={pathwaySearchForm}
                                    selectLevel="concept"
                                    defaultLevel="syllabus"
                                    onLoadComplete={onLoadComplete}
                                />
                            </TableFilterControlsSection>
                        </TableFilterControlsForm>
                    } />
                {getTableButtons()}
                <TableAntD
                    rowKey="pathwayId"
                    pagingDependencies={[]}
                    reload={reload}
                    pagingData={{fetchFunction: fetchData, params: {pageSize: 10, currentPage: 1}}}
                    columnData={columns}
                    noDataMessage={<span className="ch-warning-text">No {brandConfig.tasksName} found</span>}
                />
                {getTableButtons()}
            </div>
            <ShareTrailDialog
                open={isShareDialogOpen}
                onClose={() => setIsShareDialogOpen(false)}
                tasksName={brandConfig.tasksName}
                taskName={brandConfig.taskName}
                selectedTrails={selectedTrails}
            />
            <AssignTrailDialog
                open={isAssignTrailDialogOpen}
                onClose={() => setAssignTrailDialogOpen(false)}
                setReload={handleSearch}
                selectedTrail={selectedTrail}
            />
        </div>
    );
};

export default TeacherTrails;